import { sectionLabels } from "../section-labels";
/**
 * The restaurant hero — the one dish or one sentence the page leads with.
 *
 * Two `layout`s, because the six artboards split cleanly in half and no amount
 * of setting-tweaking turns one into the other:
 *
 *  - `split`  — headline and CTA beside a framed photo. STACK, HOMEGRILL, VERDE.
 *  - `plate`  — a full-bleed photo of ONE dish with the name, price and a "tap
 *               to order" affordance laid over its foot. KOYO, OTTO, BRÜ.
 *
 * The photo is the FEATURED MENU ITEM's own image by default rather than an
 * upload. A restaurant that changes its hero dish should do it by changing the
 * dish, not by re-uploading a picture into a second place that then disagrees
 * with the menu — which is how a hero ends up advertising a dish the kitchen
 * stopped making.
 */
export const restaurantHeroSchema = {
    type: "restaurant-hero",
    name: sectionLabels["restaurant-hero"],
    description: {
        ar: "الطبق أو الجملة اللي بتفتح بيها الصفحة.",
        en: "The dish or the sentence the page opens with.",
    },
    maxPerTemplate: 1,
    availableOnTemplates: ["home"],
    availableOnStoreKinds: ["RESTAURANT"],
    settings: [
        {
            id: "layout",
            type: "select",
            label: { ar: "الشكل", en: "Layout" },
            options: [
                { value: "split", label: { ar: "نص وصورة", en: "Text beside photo" } },
                { value: "plate", label: { ar: "طبق بعرض الشاشة", en: "Full-bleed dish" } },
            ],
            default: "split",
        },
        {
            id: "headline",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Headline" },
            helpText: {
                ar: "جملة قصيرة عن المطعم. حُط أي كلمة بين [أقواس] عشان تطلع بلون المطعم.",
                en: "A short line about the restaurant. Put any word in [brackets] to print it in the brand colour.",
            },
            validate: { max: 80 },
            showIf: { layout: "split" },
        },
        {
            id: "subhead",
            type: "textarea",
            i18n: true,
            label: { ar: "سطر تحت العنوان", en: "Supporting line" },
            validate: { max: 160 },
            showIf: { layout: "split" },
        },
        {
            /**
             * A picker, not free text: the hero's price and photo have to be the
             * item's real ones, and an id is the only way to keep them so as the
             * menu changes. Empty ⇒ the menu's first item, so a store that has
             * never touched this still gets a hero rather than a hole.
             */
            id: "featuredItem",
            type: "productPicker",
            label: { ar: "الطبق المميز", en: "Featured dish" },
            helpText: {
                ar: "سيبه فاضي عشان ياخد أول طبق في المنيو.",
                en: "Leave empty to lead with the first item on the menu.",
            },
        },
        {
            /**
             * The two hero buttons. The primary one is a PICKUP action (Open
             * Decision 1) and renders inert, badged in preview; the secondary just
             * scrolls to the menu and is real.
             */
            id: "primaryCta",
            type: "text",
            i18n: true,
            label: { ar: "الزر الأساسي", en: "Primary button" },
            helpText: {
                ar: "الطلب المسبق لسه مش شغّال — الزر بيتعرض من غير ما يشتغل.",
                en: "Order-ahead is not built yet — the button renders but does nothing.",
            },
            validate: { max: 24 },
            showIf: { layout: "split" },
        },
        {
            id: "secondaryCta",
            type: "text",
            i18n: true,
            label: { ar: "الزر الثاني", en: "Secondary button" },
            helpText: {
                ar: "بينزّل الصفحة على المنيو.",
                en: "Scrolls down to the menu.",
            },
            validate: { max: 24 },
            showIf: { layout: "split" },
        },
        /*
         * Up to three authored figures under the hero — VERDE's
         * `620 AVG CALORIES · 14 LOCAL FARMS · 15min AVG PICKUP` row.
         *
         * AUTHORED, never computed. The platform stores no calorie count, no
         * supplier records (§9 fences them out) and no pickup timing, so anything
         * derived here would be invented — and an invented average calorie figure
         * is a health claim made on the restaurant's behalf. A pair with either
         * half missing is dropped, so a restaurant that fills in none gets no row.
         */
        {
            id: "stat1Value",
            type: "text",
            i18n: true,
            label: { ar: "رقم 1", en: "Stat 1 — number" },
            helpText: {
                ar: "رقم قصير — «٦٢٠» أو «١٥ دقيقة».",
                en: 'A short figure — "620" or "15min".',
            },
            validate: { max: 12 },
        },
        {
            id: "stat1Label",
            type: "text",
            i18n: true,
            label: { ar: "وصف رقم 1", en: "Stat 1 — label" },
            validate: { max: 24 },
        },
        {
            id: "stat2Value",
            type: "text",
            i18n: true,
            label: { ar: "رقم 2", en: "Stat 2 — number" },
            helpText: {
                ar: "رقم قصير — «٦٢٠» أو «١٥ دقيقة».",
                en: 'A short figure — "620" or "15min".',
            },
            validate: { max: 12 },
        },
        {
            id: "stat2Label",
            type: "text",
            i18n: true,
            label: { ar: "وصف رقم 2", en: "Stat 2 — label" },
            validate: { max: 24 },
        },
        {
            id: "stat3Value",
            type: "text",
            i18n: true,
            label: { ar: "رقم 3", en: "Stat 3 — number" },
            helpText: {
                ar: "رقم قصير — «٦٢٠» أو «١٥ دقيقة».",
                en: 'A short figure — "620" or "15min".',
            },
            validate: { max: 12 },
        },
        {
            id: "stat3Label",
            type: "text",
            i18n: true,
            label: { ar: "وصف رقم 3", en: "Stat 3 — label" },
            validate: { max: 24 },
        },
        {
            id: "eyebrow",
            type: "text",
            i18n: true,
            label: { ar: "كلمة فوق العنوان", en: "Eyebrow" },
            helpText: {
                ar: "كلمتين صغيرين فوق العنوان — «طبق النهارده» مثلًا.",
                en: 'Two small words above the headline — "Tonight’s plate".',
            },
            validate: { max: 32 },
        },
    ],
};
//# sourceMappingURL=restaurant-hero.js.map